import { useTranslation } from "react-i18next";

import { intlLocale } from "@/i18n";
import { cn } from "@/lib/utils";

/** The API money shape (design/07 §1): decimal string + ISO currency. Never a float. */
export interface MoneyValue {
  amount: string;
  currencyCode: string;
}

/** A money prop: API object, integer string ("19000") or bigint — `number` is not accepted. */
export type MoneyInput = MoneyValue | string | bigint;

const INTEGER = /^-?\d+$/;
const DECIMAL = /^-?\d+(\.\d+)?$/;
/** Currencies without minor units (amounts must be whole numbers). */
const ZERO_DECIMAL = new Set(["JPY", "KRW", "VND", "TWD"]);

function parts(value: MoneyInput, currency: string | undefined): { amount: string; code: string } {
  if (typeof value === "bigint") {
    return { amount: value.toString(), code: currency ?? "JPY" };
  }
  if (typeof value === "string") {
    return { amount: value, code: currency ?? "JPY" };
  }
  return { amount: value.amount, code: value.currencyCode || currency || "JPY" };
}

/**
 * Format money exactly as the server sent it. JPY must be an integer string (the tax engine rounds
 * once per document server-side — the client never calculates); other presentment currencies may
 * carry decimals and are formatted from the decimal string (no float rounding).
 */
export function formatMoney(value: MoneyInput, language?: string, currency?: string): string {
  const { amount, code } = parts(value, currency);
  const zeroDecimal = ZERO_DECIMAL.has(code.toUpperCase());
  if (!(zeroDecimal ? INTEGER : DECIMAL).test(amount)) {
    throw new TypeError(
      `money amount must be a ${zeroDecimal ? "whole" : "decimal"} string: ${amount}`,
    );
  }
  const formatter = new Intl.NumberFormat(intlLocale(language), {
    style: "currency",
    currency: code,
    currencyDisplay: "narrowSymbol",
    ...(zeroDecimal ? { maximumFractionDigits: 0, minimumFractionDigits: 0 } : {}),
  });
  // `format` takes an exact decimal string (Intl.NumberFormat v3) or a bigint — never a float
  const exact: bigint | Intl.StringNumericLiteral = INTEGER.test(amount)
    ? BigInt(amount)
    : (amount as Intl.StringNumericLiteral);
  // ja-JP prints the full-width "￥"; the shop always shows "¥"
  return formatter.format(exact).replace("￥", "¥");
}

export interface MoneyProps {
  value: MoneyInput;
  /** Currency for string/bigint values (default JPY). */
  currency?: string;
  /** Show the small 税込 badge (総額表示 — shop prices are always tax-included). */
  taxIncluded?: boolean;
  /** Tax-exclusive amount **as returned by the API** — shown as "(税抜 ¥17,273)". */
  priceExclTax?: MoneyInput;
  className?: string;
}

export function Money({ value, currency, taxIncluded, priceExclTax, className }: MoneyProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage;
  return (
    <span
      data-slot="money"
      className={cn("inline-flex items-baseline gap-1 tabular-nums", className)}
    >
      <span className="font-semibold">{formatMoney(value, language, currency)}</span>
      {taxIncluded ? (
        <span className="rounded-sm border px-1 text-[0.7em] leading-tight text-muted-foreground">
          {t("money.tax_included")}
        </span>
      ) : null}
      {priceExclTax !== undefined ? (
        <span className="text-xs text-muted-foreground">
          ({t("money.tax_excluded", { amount: formatMoney(priceExclTax, language, currency) })})
        </span>
      ) : null}
    </span>
  );
}
